<template>
  <div class="bilibili-video-container">
    <template v-if="!permissionDenied">
      <div v-if="video !== null" class="video-page-wrap">

        <!-- 1. 顶部视频播放器 -->
        <div class="video-player-box">
          <video-player-card
            v-if="video"
            :video-id="videoId"
            :video-data="video"
            :user-token="userToken"
            :send-event="sendEvent"
            @update-view-count="val => realtimeViewCount = val"
          />
        </div>

        <!-- 2. 简介 / 评论 切换 Tab -->
        <div class="nav-tab-bar">
          <div class="tab-left">
            <div
              class="tab-item"
              :class="{ active: activeTab === 'intro' }"
              @click="activeTab = 'intro'"
            >
              简介
            </div>
            <div
              class="tab-item"
              :class="{ active: activeTab === 'comment' }"
              @click="switchTabToComment"
            >
              评论 <span class="count">{{ video.comment }}</span>
            </div>
          </div>
        </div>

        <!-- Tab 1: 简介面板 -->
        <div v-show="activeTab === 'intro'" class="tab-content-intro">
          <!-- UP 主信息栏 -->
          <div v-if="user" class="up-info-bar">
            <!-- 点击头像和信息区域都可以跳转到用户主页 -->
            <div class="up-left-clickable" @click="goToUserProfile">
              <div class="up-avatar-wrap">
                <img :src="user.avatarUrl" class="up-avatar" alt="avatar">
              </div>
              <div class="up-detail">
                <div class="up-name">{{ user.screenName }}</div>
                <div class="up-sub">
                  <span>{{ formatCount(user.follower) }}粉丝</span>
                  <span>{{ user.videoCount || 1024 }}视频</span>
                </div>
              </div>
            </div>

            <button class="follow-btn" @click="handleFollow">
              <i class="el-icon-plus" /> 关注
            </button>
          </div>

          <!-- 视频标题与展开折叠 -->
          <div class="video-title-box">
            <h1 class="video-title" v-html="video.title" />
          </div>

          <!-- 视频元数据 -->
          <div class="video-meta-row">
            <span class="meta-item"><i class="el-icon-video-play" /> {{ formatCount(video.view) }}</span>
            <span class="meta-item"><i class="el-icon-chat-line-square" /> {{ formatCount(video.comment) }}</span>
            <span class="meta-item">{{ video.pubDate }}</span>
            <span class="meta-item"><i class="el-icon-user" /> {{ realtimeViewCount || 5 }}人正在看</span>
          </div>

          <!-- 展开后的详细描述与标签 -->
          <div class="expanded-desc-box">
            <div class="video-description" v-html="video.description" />
            <div v-if="video.tags && video.tags.length > 0" class="video-tags">
              <el-tag v-for="(tag,index) in video.tags" :key="index" class="video-tag-item" size="small" effect="light" round>
                <router-link :to="'/video/tag/' + tag"># {{ tag }}</router-link>
              </el-tag>
            </div>
          </div>

          <!-- 操作交互栏 -->
          <div class="ops-action-bar">
            <div class="action-item" @click="likeVideo(video)">
              <i class="el-icon-top" />
              <span>{{ formatCount(video.thumbUp) }}</span>
            </div>
            <div class="action-item" @click="dislikeVideoWrapper(video)">
              <i class="el-icon-bottom" />
              <span>不喜欢</span>
            </div>
            <div class="action-item" @click="$message.info('投币成功')">
              <i class="el-icon-coin" />
              <span>{{ formatCount(video.thumbDown) }}</span>
            </div>
            <div class="action-item" @click="collection(video)">
              <i class="el-icon-star-off" />
              <span>{{ formatCount(video.favorite) }}</span>
            </div>
            <div class="action-item" @click="displayShareVideoDialog">
              <i class="el-icon-share" />
              <span>{{ formatCount(video.share) }}</span>
            </div>
          </div>

          <!-- 推荐视频列表 -->
          <div class="recommend-list-section">
            <div
              v-for="(item, index) in similarVideos"
              :key="index"
              class="recommend-card"
              @click="openRecommendVideo(item)"
            >
              <div class="cover-box">
                <img :src="item.coverUrl" class="cover-img" alt="cover">
                <span v-if="item.duration" class="duration-tag">{{ item.duration }}</span>
              </div>
              <div class="info-box">
                <h4 class="rec-title">{{ item.title }}</h4>
                <div class="rec-author">
                  <span class="up-tag">UP</span>
                  <span class="author-name">{{ item.user ? item.user.screenName : '匿名UP主' }}</span>
                </div>
                <div class="rec-meta">
                  <span class="meta"><i class="el-icon-video-play" /> {{ formatCount(item.view) }}</span>
                  <span class="meta"><i class="el-icon-chat-line-square" /> {{ formatCount(item.comment) }}</span>
                </div>
              </div>
              <div class="more-btn" @click.stop="$message.info('更多选项')">
                <i class="el-icon-more" />
              </div>
            </div>
          </div>
        </div>

        <!-- Tab 2: 评论区面板 (只有当 commentLoaded 为 true 时才挂载渲染并向后端请求评论) -->
        <div v-show="activeTab === 'comment'" class="tab-content-comment">
          <user-comment-card
            v-if="videoId && commentLoaded"
            ref="commentCard"
            :post-id="videoId"
            post-type="VideoPost"
            :current-user="currentUser"
            :screen-width="screenWidth"
          />
        </div>

        <!-- 收藏夹弹窗 -->
        <el-dialog append-to-body :visible.sync="showPlaylistDialog" title="保存到收藏夹" width="90%" center custom-class="mobile-dialog">
          <el-table :data="playlist" highlight-current-row @current-change="handleCurrentRow">
            <el-table-column type="index" width="40" />
            <el-table-column prop="albumName" label="收藏夹名称" />
            <el-table-column prop="total" label="内容数" width="70" />
          </el-table>
          <div class="new-album-input">
            <el-input v-model="albumForm.albumName" placeholder="新建收藏夹..." size="medium">
              <el-button slot="append" icon="el-icon-plus" @click="onCreateAlbum">创建</el-button>
            </el-input>
          </div>
          <span slot="footer">
            <el-button size="small" @click="showPlaylistDialog = false">取消</el-button>
            <el-button size="small" type="primary" @click="addToPlaylist">确定收藏</el-button>
          </span>
        </el-dialog>

        <!-- 视频报错弹窗 -->
        <el-dialog append-to-body :visible.sync="showErrorReportDialog" title="视频报错" width="90%" custom-class="mobile-dialog">
          <el-form :model="errorReportForm" label-position="top">
            <el-form-item label="请选择错误类型">
              <el-select v-model="errorReportForm.errorCode" placeholder="请选择" style="width: 100%">
                <el-option label="视频无封面" value="1" /><el-option label="视频无声音" value="2" />
                <el-option label="视频无画面" value="3" /><el-option label="视频无资源" value="4" />
                <el-option label="视频有广告" value="5" />
              </el-select>
            </el-form-item>
          </el-form>
          <span slot="footer">
            <el-button size="small" @click="showErrorReportDialog = false">取消</el-button>
            <el-button size="small" type="primary" @click="submitErrorReport">提交反馈</el-button>
          </span>
        </el-dialog>

        <!-- 分享视频弹窗 -->
        <el-dialog append-to-body :visible.sync="showShareVideoDialog" title="分享视频" width="90%" custom-class="mobile-dialog">
          <div class="share-preview">
            <el-image lazy fit="cover" class="share-cover" :src="video.coverUrl" />
            <h4 v-html="video.title" />
          </div>
          <span slot="footer">
            <el-button size="small" type="primary" icon="el-icon-document-copy" @click="submitShareVideo">复制分享链接</el-button>
          </span>
        </el-dialog>

      </div>
    </template>
    <template v-else>
      <div class="denied-container">
        <permission-denied-card :text-object="textObject" />
      </div>
    </template>
  </div>
</template>

<script>
import { similarVideo, videoInfo, downloadVideo, getShortUrl, videoErrorReport } from '@/api/vod'
import { doAlbumAction, createAlbum, getUserAlbumList } from '@/api/vod'
import { dislikeVideo } from '@/api/vod'

import PermissionDeniedCard from '@/components/card/PermissionDeniedCard.vue'
import VideoPlayerCard from 'components/card/VideoPlayerCard'
import UserCommentCard from '@/components/cardm/UserCommentCard.vue'

export default {
  name: 'VideoPageMobile',
  components: { PermissionDeniedCard, VideoPlayerCard, UserCommentCard },
  data() {
    return {
      activeTab: 'intro',
      commentLoaded: false, // ★ 新增：标记评论是否已经加载过
      screenWidth: document.body.clientWidth,
      videoId: null,
      video: null,
      user: null,
      similarVideos: [],
      currentUser: { userId: -1, name: '游客', avatar: '', author: false },
      isCollected: false,
      showPlaylistDialog: false,
      playlist: [],
      showErrorReportDialog: false,
      errorReportForm: { videoId: null, errorCode: null },
      permissionDenied: false,
      textObject: { content: '视频', route: '/video' },
      albumForm: { albumName: null },
      currentRow: null,
      showShareVideoDialog: false,
      showEdit: true,
      userToken: null,
      sendEvent: true,
      realtimeViewCount: 0
    }
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.videoId = newId
          this.commentLoaded = false // 切换视频时重置评论加载状态
          this.activeTab = 'intro' // 切换视频时默认回到简介
          this.getVideoInfo(this.videoId)
          this.getSimilarVideos(this.videoId)
        }
      }
    }
  },
  created() {
    this.userToken = ''
    const loginUser = this.$store.state.userInfo
    if (loginUser) {
      this.currentUser = { userId: loginUser.userIdStr, name: loginUser.screenName, avatar: loginUser.avatarUrl, author: true }
    }
  },
  methods: {
    // ★ 切换到评论 Tab 触发的懒加载逻辑
    switchTabToComment() {
      this.activeTab = 'comment'
      if (!this.commentLoaded) {
        this.commentLoaded = true
        // 如果你的 UserCommentCard 内部是在 created/mounted 自动请求，
        // 那么通过将 commentLoaded 设为 true 挂载组件即可自动触发。
        // 如果需要手动调用方法加载，可以在此添加对应的请求函数。
      }
    },
    formatCount(n) {
      if (!n) return '0'
      return n > 10000 ? (n / 10000).toFixed(1) + '万' : n
    },
    handleFollow() {
      this.$message.success('已关注 UP 主')
    },
    openRecommendVideo(item) {
      this.$router.push(`/video/${item.videoId}`)
    },
    handleCurrentRow(val) { this.currentRow = val },
    collection(video) {
      if (video.collected) return this.$message.info('取消收藏')
      this.showPlaylistDialog = true
      const queryInfo = {
        userId: this.currentUser.userId,
        postType: 2,
        pn: 1
      }
      getUserAlbumList(queryInfo).then(resp => { if (resp && resp.code === 0) this.playlist = resp.data.list })
    },
    addToPlaylist() {
      if (!this.currentRow) return this.$message.info('请选择收藏夹')
      doAlbumAction({ albumId: this.currentRow.albumId, postId: this.videoId, action: 1 }).then(resp => {
        if (resp && resp.code === 0) this.$notify.success({ title: '视频已收藏' })
        this.showPlaylistDialog = false
      })
    },
    getVideoInfo(videoId) {
      videoInfo(videoId).then(resp => {
        if (resp.code === 0) {
          this.video = resp.data
          this.user = resp.data.user
          document.title = resp.data.title
        } else if (resp && resp.code === 2) {
          this.$router.push('/404')
        } else {
          this.permissionDenied = true
        }
      })
    },
    getSimilarVideos(videoId) {
      similarVideo(videoId).then(resp => { if (resp && resp.code === 0) this.similarVideos = resp.data || [] })
    },
    likeVideo(video) { this.$message.success('点赞成功') },
    dislikeVideoWrapper(video) {
      dislikeVideo({ videoId: video.videoId }).then(resp => { if (resp && resp.code === 0) this.$message.info('数据已提交') })
    },
    getDownloadUrl(videoId) {
      downloadVideo(videoId).then(resp => {
        if (resp && resp.code === 0) window.open(resp.data.url, '_blank')
      })
    },
    onCreateAlbum() {
      createAlbum(this.albumForm).then(resp => { if (resp && resp.code === 0) this.playlist.push(resp.data) })
      this.albumForm.albumName = null
    },
    displayErrorReportDialog() { this.errorReportForm.videoId = this.video.videoId; this.showErrorReportDialog = true },
    submitErrorReport() {
      videoErrorReport(this.errorReportForm).then(resp => {
        if (resp && resp.code === 0) this.$notify.warning({ title: '提示', message: '视频错误已提交' })
        this.showErrorReportDialog = false
      })
    },
    displayShareVideoDialog() { this.showShareVideoDialog = true },
    submitShareVideo() {
      getShortUrl(this.video.videoId).then(resp => {
        if (resp.code === 0) {
          const content = window.location.origin + resp.data
          const input = document.createElement('input'); input.value = content; document.body.appendChild(input)
          input.select(); document.execCommand('Copy'); document.body.removeChild(input)
          this.$message.info('已成功复制到剪贴板')
        }
        this.showShareVideoDialog = false
      })
    },
    goToUserProfile() {
      if (this.user && this.user.userId) {
        this.$router.push(`/user/${this.user.userId}`)
      }
    }
  }
}
</script>

<style scoped lang="scss">
.bilibili-video-container {
  min-height: 100vh;
  background-color: #ffffff;
  color: #212121;
  font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, Arial, sans-serif;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.video-player-box {
  width: 100%;
  background-color: #000000;
  position: sticky;
  top: 0;
  z-index: 100;
}

.nav-tab-bar {
  display: flex;
  align-items: center;
  height: 44px;
  padding: 0 12px;
  border-bottom: 1px solid #e7e7e7;
  background-color: #ffffff;

  .tab-left {
    display: flex;
    gap: 24px;

    .tab-item {
      font-size: 14px;
      color: #757575;
      position: relative;
      line-height: 44px;
      cursor: pointer;

      .count {
        font-size: 11px;
        color: #999;
      }

      &.active {
        color: #fb7299;
        font-weight: bold;

        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 20px;
          height: 2px;
          background-color: #fb7299;
          border-radius: 1px;
        }
      }
    }
  }
}

.tab-content-intro {
  padding: 12px;
}

.up-info-bar {
  display: flex;
  align-items: center;
  margin-bottom: 12px;

  .up-avatar-wrap {
    margin-right: 10px;
    .up-avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      object-fit: cover;
    }
  }

  .up-detail {
    flex: 1;

    .up-name {
      font-size: 14px;
      color: #fb7299;
      font-weight: 500;
      margin-bottom: 2px;
    }

    .up-sub {
      font-size: 11px;
      color: #999999;
      span {
        margin-right: 8px;
      }
    }
  }

  .follow-btn {
    background-color: #fb7299;
    color: #ffffff;
    border: none;
    padding: 6px 16px;
    border-radius: 16px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 2px;

    &:active {
      opacity: 0.85;
    }
  }
}

.video-title-box {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 6px;

  .video-title {
    font-size: 15px;
    font-weight: 600;
    color: #212121;
    line-height: 1.4;
    margin: 0;
    flex: 1;
    padding-right: 10px;
  }

  .expand-btn {
    font-size: 16px;
    color: #999999;
    padding-top: 2px;
    cursor: pointer;
  }
}

.video-meta-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 11px;
  color: #999999;
  margin-bottom: 14px;

  .meta-item {
    display: flex;
    align-items: center;
    gap: 2px;

    i {
      font-size: 13px;
    }
  }
}

.expanded-desc-box {
  font-size: 12px;
  color: #616161;
  line-height: 1.6;
  margin-bottom: 12px;
  background-color: #f9f9f9;
  padding: 8px;
  border-radius: 6px;

  .video-tags {
    margin-top: 8px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;

    .tag-pill {
      background-color: #ffffff;
      color: #fb7299;
      padding: 2px 8px;
      border-radius: 10px;
      border: 1px solid #fce4ec;
    }
  }

  .video-tag-item { margin-right: 10px; margin-bottom: 8px; border: none; background: #f0f2f5; }
  .video-tag-item a { text-decoration: none; color: #409EFF; }
}

.ops-action-bar {
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 10px 0;
  margin-bottom: 12px;

  .action-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    color: #757575;
    font-size: 11px;
    cursor: pointer;

    i {
      font-size: 22px;
      margin-bottom: 4px;
      color: #757575;
    }

    &:active {
      color: #fb7299;
      i { color: #fb7299; }
    }
  }
}

.recommend-list-section {
  .recommend-card {
    display: flex;
    margin-bottom: 12px;
    position: relative;
    cursor: pointer;

    .cover-box {
      position: relative;
      width: 115px;
      height: 72px;
      border-radius: 6px;
      overflow: hidden;
      background-color: #f0f0f0;
      flex-shrink: 0;
      margin-right: 10px;

      .cover-img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .duration-tag {
        position: absolute;
        bottom: 4px;
        right: 4px;
        background: rgba(0, 0, 0, 0.7);
        color: #ffffff;
        font-size: 10px;
        padding: 1px 4px;
        border-radius: 2px;
      }
    }

    .info-box {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      .rec-title {
        font-size: 13px;
        font-weight: 500;
        color: #212121;
        margin: 0;
        line-height: 1.35;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      }

      .rec-author {
        display: flex;
        align-items: center;
        font-size: 11px;
        color: #999999;
        margin-top: 2px;

        .up-tag {
          border: 1px solid #999999;
          font-size: 9px;
          padding: 0 2px;
          border-radius: 2px;
          margin-right: 4px;
          line-height: 1.1;
        }

        .author-name {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }

      .rec-meta {
        display: flex;
        gap: 12px;
        font-size: 11px;
        color: #999999;

        .meta {
          display: flex;
          align-items: center;
          gap: 2px;
          i { font-size: 12px; }
        }
      }
    }

    .more-btn {
      position: absolute;
      right: 0;
      bottom: 2px;
      color: #999999;
      font-size: 14px;
      padding: 4px;
    }
  }
}

.tab-content-comment {
  padding: 10px;
}

.share-preview {
  text-align: center;
  .share-cover {
    width: 100%;
    height: 160px;
    border-radius: 6px;
    margin-bottom: 10px;
    object-fit: cover;
  }
}
</style>

<style lang="scss">
.mobile-dialog {
  border-radius: 12px !important;
  max-width: 380px;

  .el-dialog__body {
    padding: 10px 15px !important;
  }
}

.up-left-clickable {
  display: flex;
  align-items: center;
  flex: 1;
  cursor: pointer;
  overflow: hidden;

  &:active {
    opacity: 0.8;
  }
}
</style>
